// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  setFunds,
  setActiveFilters,
  setActiveSorting,
  clearActiveFilters,
  appendFunds
} from '../../../state_manager/state_update_actions/funds/index.ts'
import { FilterValue, SortConfig } from '../../../state_manager/interfaces/funds.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  fetchFundsFiltered,
  fetchFundsFilterOptions,
  FundsPaginatedResponse
} from '../services/fundsService.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  DEFAULT_FUNDS_TABLE_FILTERS,
  areSortConfigsEqual,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow
} from '../../global_configs/tableViewPreferences.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import { MENU_ITEM_KEY_FUNDS } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useFundsReportId } from '../../../state_manager/hooks/useFundsReportId.ts'

export const useFundsData = () => {
  const dispatch = useDispatch<AppDispatch>()

  const { menuItemId, preferenceRow, persistPreference, deletePreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_FUNDS
  )

  const fundsReportId = useFundsReportId()
  const defaultTableSorting = useReportingDefaultSorting(fundsReportId)

  const [fundsDataLoading, setFundsDataLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [prefsReady, setPrefsReady] = useState(false)
  const [filterOptions, setFilterOptions] = useState<
    Record<string, Array<{ value: string; label: string }>>
  >({})
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const skipPersistRef = useRef(true)

  const fundsData = useSelector((state: any) => state.funds?.data ?? [])
  const activeFilters = useSelector((state: any) => state.funds?.activeFilters ?? [])
  const activeSorting = useSelector((state: any) => state.funds?.activeSorting ?? [])
  const hasMore = useSelector((state: any) => state.funds?.hasMore ?? false)
  const currentPage = useSelector((state: any) => state.funds?.currentPage ?? 1)

  const menuItems = useSelector((state: any) => state.globalConfigs?.menu_items ?? [])

  // Resolve user preferences once globalConfigs has loaded.
  useEffect(() => {
    if (menuItems.length === 0) return
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_FUNDS_TABLE_FILTERS }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting }))
      setPrefsReady(true)
      return
    }
    const filters = resolveTableFiltersFromPreferenceRow(preferenceRow, DEFAULT_FUNDS_TABLE_FILTERS)
    const sorting = resolveTableSortingFromPreferenceRow(preferenceRow, defaultTableSorting)
    if (!areFilterValuesEqual(filters, activeFilters)) {
      dispatch(setActiveFilters({ filters }))
    }
    if (!areSortConfigsEqual(sorting, activeSorting)) {
      dispatch(setActiveSorting({ sorting }))
    }
    setPrefsReady(true)
  }, [menuItemId, menuItems.length, preferenceRow, defaultTableSorting, dispatch])

  const handleFetchFunds = useCallback(
    async (sorting: SortConfig[] = [], filters?: FilterValue[]) => {
      try {
        setFundsDataLoading(true)
        const currentToken = getAccessToken(store.getState().auth)
        const response: FundsPaginatedResponse = await fetchFundsFiltered(
          sorting,
          filters ?? [],
          1,
          null,
          currentToken,
          { reportId: fundsReportId }
        )
        dispatch(setFunds({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching funds:', e)
        }
      } finally {
        setFundsDataLoading(false)
      }
    },
    [dispatch, fundsReportId]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      dispatch(setActiveFilters({ filters }))
    },
    [dispatch, activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      dispatch(setActiveSorting({ sorting }))
    },
    [dispatch, activeSorting]
  )

  const handleClearFilters = useCallback(() => {
    dispatch(clearActiveFilters())
  }, [dispatch])

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_FUNDS_TABLE_FILTERS }))
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to delete funds table preferences:', result.error)
        }
        return
      }
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      skipPersistRef.current = true
      dispatch(setActiveFilters({ filters: DEFAULT_FUNDS_TABLE_FILTERS }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting }))
    })()
  }, [dispatch, menuItemId, deletePreference, defaultTableSorting])

  const persistMergedPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const merged: Record<string, unknown> = {
        ...(row?.data ?? {}),
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const base: Record<string, unknown> = { ...(row?.data ?? {}) }
    delete base[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...base,
      [PREF_TABLE_FILTERS]: activeFilters,
      [PREF_TABLE_SORTING]: activeSorting
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      if (hadExistingRow) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      } else {
        const user = store.getState().auth.user
        if (user) {
          await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const handleClearSorting = useCallback(() => {
    dispatch(setActiveSorting({ sorting: defaultTableSorting }))
  }, [dispatch, defaultTableSorting])

  const handleLoadMore = useCallback(
    async (page: number, pageSize: number) => {
      if (loadingMore || !hasMore) return
      try {
        setLoadingMore(true)
        const currentToken = getAccessToken(store.getState().auth)
        const response = await fetchFundsFiltered(
          activeSorting,
          activeFilters,
          page,
          pageSize,
          currentToken,
          { reportId: fundsReportId }
        )
        dispatch(appendFunds({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading more funds:', e)
        }
      } finally {
        setLoadingMore(false)
      }
    },
    [dispatch, activeFilters, activeSorting, hasMore, loadingMore, fundsReportId]
  )

  // Fetch filter-option lists once prefs (and therefore auth) are ready.
  // The option set is keyed by the effective `filter_key` from cfg
  // (e.g. `funds__id` for the Name multiselect) and is consumed by
  // TableView's column filters. Gating on prefsReady mirrors handleFetchFunds
  // so we don't fire before the auth token is hydrated into redux.
  useEffect(() => {
    if (!prefsReady) return
    const token = getAccessToken(store.getState().auth)
    if (!token) return
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      const res = await fetchFundsFilterOptions(token)
      if (!cancelled) {
        setFilterOptions(res.data || {})
        setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [prefsReady])

  // Refetch whenever filters/sorting change (after prefs are ready).
  useEffect(() => {
    if (!prefsReady) return
    const currentToken = getAccessToken(store.getState().auth)
    if (currentToken) {
      handleFetchFunds(activeSorting, activeFilters)
    }
  }, [prefsReady, activeFilters, activeSorting, handleFetchFunds])

  // Persist filter/sort changes to user preferences (skip the first render
  // since it just hydrated from prefs).
  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist funds table preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedPreference])

  // Backwards-compatible aliases so existing callers keep working.
  return {
    fundsData,
    data: fundsData,
    loading: fundsDataLoading,
    fundsDataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetchFunds,
    refetch: () => handleFetchFunds(activeSorting, activeFilters),
    handleSetFilters,
    handleSetSorting,
    handleClearFilters,
    handleClearSorting,
    handleLoadMore,
    defaultTableFilters: DEFAULT_FUNDS_TABLE_FILTERS,
    defaultTableSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    error: null as string | null
  }
}
